import React from 'react';
import styled from 'styled-components';

import { Button } from '@rsc/ui-components/shadcn/Button';
import breakpoints from '@rsc/ui-components/breakpoints';
import sharedStyles from '@rsc/ui-components/styles';
import { openMavenoidChat } from '@rsc/ui-components/openChatWidget';

import { BsTelephone, BsGeoAlt } from 'react-icons/bs';
import { PiChatCircleDots } from 'react-icons/pi';

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column-reverse;
  flex-flow: column;
  margin: ${sharedStyles.spacing['spacing-md']} 0 0;
  gap: ${sharedStyles.spacing['spacing-md']};

  a {
    display: block;
    color: inherit;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }

  @media (min-width: ${breakpoints.lg}) {
    flex-direction: row;
    flex-flow: row;
  }
`;

const AddressContainer = styled.div`
  flex: 1 0 0;
  margin-top: ${sharedStyles.spacing['spacing-md']};

  @media (min-width: ${breakpoints.lg}) {
    margin-top: ${sharedStyles.spacing['spacing-sm']};
  }
`;

const PhoneNumber = styled.a`
  margin-top: ${sharedStyles.spacing['spacing-md']};

  @media (min-width: ${breakpoints.md}) {
    margin-top: ${sharedStyles.spacing['spacing-sm']};
  }
`;

const IconContainer = styled.div`
  display: inline-block;
  vertical-align: top;

  svg {
    display: flex;
    margin-right: ${sharedStyles.spacing['spacing-sm']};

    path {
      stroke-width: 0.5px;
    }
  }
`;

const CallUsInfoContainer = styled.div`
  margin-bottom: ${sharedStyles.spacing['spacing-md']};
  margin-left: calc(1.5em + ${sharedStyles.spacing['spacing-sm']});

  @media (min-width: ${breakpoints.md}) {
    margin-bottom: ${sharedStyles.spacing['spacing-sm']};
  }
`;

export interface DealerAddressProps {
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phoneNumber?: string;
  isCompanyOwned?: boolean;
  codCallHours?: string;
}

function DealerAddress({
  addressLine1 = '',
  addressLine2 = '',
  addressLine3 = '',
  city = '',
  state = '',
  zip = '',
  country = '',
  phoneNumber = '',
  isCompanyOwned = false,
  codCallHours = '',
}: Readonly<DealerAddressProps>) {
  const formatPhoneNumber = number =>
    `tel:${number
      .replaceAll('(', '')
      .replaceAll(')', '')
      .replaceAll(' ', '')
      .replaceAll('-', '')}`;

  return (
    <Container>
      <div className="flex-1">
        {phoneNumber && (
          <PhoneNumber href={formatPhoneNumber(phoneNumber)}>
            <IconContainer>
              <BsTelephone size={24} aria-hidden="true" />
            </IconContainer>
            {phoneNumber}
          </PhoneNumber>
        )}
        {isCompanyOwned && (
          <>
            <CallUsInfoContainer>
              <p>Call us anytime</p>
              <p>{codCallHours}</p>
            </CallUsInfoContainer>
            <Button
              onClick={openMavenoidChat}
              className="p-0 h-auto gap-2.5 text-base font-normal underline hover:bg-primary hover:no-underline"
            >
              <PiChatCircleDots size={24} aria-hidden="true" />
              Start a Chat
            </Button>
          </>
        )}
      </div>

      {(addressLine1 || addressLine2 || addressLine3) && (
        <AddressContainer>
          <IconContainer>
            <BsGeoAlt size={24} aria-hidden="true" />
          </IconContainer>
          <div className="inline-block">
            {addressLine1 && <p>{addressLine1}</p>}
            {addressLine2 && <p>{addressLine2}</p>}
            {addressLine3 && <p>{addressLine3}</p>}
            {city && state && (
              <p>
                {city}, {state} {zip}
              </p>
            )}
            <p>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${
                  addressLine1 || ''
                }+${addressLine2 || ''}+${city}+${state}+${zip}+${country}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Directions
              </a>
            </p>
          </div>
        </AddressContainer>
      )}
    </Container>
  );
}

export default DealerAddress;
